import { useState, useEffect, useMemo } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { FormModal } from "@/components/form-modal";
import { ParticipantSearchInput } from "@/components/participant-search-input";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronDown } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { instrumentOptions, tierOptions } from "@shared/schema";
import type { Performer, Participant, JamNight } from "@shared/schema";

const formSchema = z.object({
  participantId: z.number().optional(),
  name: z.string().min(1, "Name is required"),
  email: z.string().email("Invalid email address").optional().or(z.literal("")),
  phone: z.string().optional(),
  instagram: z.string().optional(),
  instruments: z.array(z.string()).min(1, "Please select at least one instrument"),
  tier: z.string().optional(),
  priority: z.boolean().optional().default(false),
  lookingForBand: z.boolean().optional().default(false),
  notes: z.string().optional(),
});

interface AddParticipantModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jamNightId: number;
}

export default function AddParticipantModal({ open, onOpenChange, jamNightId }: AddParticipantModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [selectedParticipant, setSelectedParticipant] = useState<Participant | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [showOtherInstrument, setShowOtherInstrument] = useState<boolean>(false);
  const [otherInstrumentValue, setOtherInstrumentValue] = useState<string>("");

  // Fetch jam night details to get organisation ID
  const { data: jamNight } = useQuery({
    queryKey: [`/api/events/${jamNightId}`],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/events/${jamNightId}`);
      return response.json();
    },
    enabled: open,
  });

  // Fetch all participants for the organisation
  const { data: participants = [] } = useQuery({
    queryKey: [`/api/participants?organisationId=${jamNight?.organisationId}`],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/participants?organisationId=${jamNight?.organisationId}`);
      const result = await response.json();
      return result;
    },
    enabled: open && !!jamNight?.organisationId,
  });

  // Fetch existing performers for this event to check for duplicates
  const { data: existingPerformers = [] } = useQuery({
    queryKey: [`/api/performers?jamNightId=${jamNightId}`],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/performers?jamNightId=${jamNightId}`);
      return response.json();
    },
    enabled: open,
  });

  // Get set of participant IDs that are already added to this event
  const existingParticipantIds = useMemo(() => {
    return new Set<number>(
      existingPerformers
        .filter((p: Performer) => p.participantId && !p.removed)
        .map((p: Performer) => p.participantId!)
    );
  }, [existingPerformers]);

  // Participants are now filtered in the ParticipantSearchInput component

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      participantId: undefined,
      name: "",
      email: "",
      phone: "",
      instagram: "",
      instruments: [],
      tier: "",
      priority: false,
      lookingForBand: false,
      notes: "",
    },
  });

  const createPerformerMutation = useMutation({
    mutationFn: async (data: z.infer<typeof formSchema>) => {
      // Get the current performers to determine the new sort order
      const currentPerformers = await apiRequest("GET", `/api/performers?jamNightId=${jamNightId}`).then(res => res.json());
      
      // Find the maximum sort order among existing performers
      const maxSortOrder = currentPerformers.reduce((max: number, p: Performer) => {
        return p.sortOrder !== undefined ? Math.max(max, p.sortOrder) : max;
      }, 0);
      
      const performerData = { 
        ...data, 
        jamNightId,
        contact: "", // Keep empty contact field for backward compatibility
        sortOrder: maxSortOrder + 1000 // Add a large gap to avoid conflicts with future reordering
      };

      const response = await apiRequest("POST", "/api/performers", performerData);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/performers?jamNightId=${jamNightId}`] });
      // Also invalidate participants query to update autocomplete
      if (jamNight?.organisationId) {
        queryClient.invalidateQueries({ queryKey: [`/api/participants?organisationId=${jamNight.organisationId}`] });
      }
      form.reset();
      setSelectedParticipant(null);
      setShowOtherInstrument(false);
      setOtherInstrumentValue("");
      onOpenChange(false);
    },
    onError: (error: any) => {
      // Check if the error is a duplicate participant error
      if (error.message === "This participant is already added to this event") {
        toast({
          title: "Duplicate Participant",
          description: "This participant is already added to this event",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to add participant",
          variant: "destructive",
        });
      }
    },
  });

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    // If "other" is selected and there's a custom value, replace "other" with the custom value
    if (showOtherInstrument && otherInstrumentValue.trim()) {
      const instruments = data.instruments.filter(i => i !== "other");
      instruments.push(otherInstrumentValue.trim());
      data.instruments = instruments;
    }
    createPerformerMutation.mutate(data);
  };

  const handleParticipantSelect = (participant: Participant) => {
    setSelectedParticipant(participant);
    form.setValue("participantId", participant.id);
    form.setValue("name", participant.name);
    form.setValue("email", participant.email || "");
    form.setValue("phone", participant.phone || "");
    form.setValue("instagram", participant.instagram || "");
    form.setValue("lookingForBand", participant.lookingForBand || false);

    // Fetch and pre-populate instruments from most recent performer record
    const fetchRecentInstruments = async () => {
      try {
        const response = await apiRequest("GET", `/api/performers/recent/${participant.id}`);
        const recentPerformer = await response.json();
        
        if (recentPerformer && recentPerformer.instruments && recentPerformer.instruments.length > 0) {
          form.setValue("instruments", recentPerformer.instruments);
          // Also pre-populate tier and priority if they exist
          if (recentPerformer.tier) {
            form.setValue("tier", recentPerformer.tier);
          }
          form.setValue("priority", recentPerformer.priority || false);
        } else {
          // No instruments found in recent performer
        }
      } catch (error: any) {
        // apiRequest throws for 404, which is expected for participants with no recent performer
        if (error.message && error.message.includes("404")) {
          // No recent performer found for participant (404), using defaults
        } else {
          // Error fetching recent performer
        }
      }
    };

    fetchRecentInstruments();
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (selectedParticipant && value !== selectedParticipant.name) {
      setSelectedParticipant(null);
      form.setValue("participantId", undefined);
    }
    form.setValue("name", value);
  };

  const handleClearSelection = () => {
    setSelectedParticipant(null);
    setSearchTerm("");
    setShowOtherInstrument(false);
    setOtherInstrumentValue("");
    form.reset();
  };

  // Dropdown handling is now in ParticipantSearchInput component

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setSelectedParticipant(null);
      setSearchTerm("");
      setShowOtherInstrument(false);
      setOtherInstrumentValue("");
      form.reset();
    }
  }, [open, form]);

  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      title="Add New Participant"
      form={form}
      onSubmit={onSubmit}
      isLoading={createPerformerMutation.isPending}
      submitLabel="Add Participant"
    >
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Participant Name</FormLabel>
            <FormControl>
              <ParticipantSearchInput
                participants={participants}
                existingParticipantIds={existingParticipantIds}
                value={searchTerm}
                onSearchChange={handleSearchChange}
                onParticipantSelect={handleParticipantSelect}
                onClear={handleClearSelection}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="instruments"
              render={() => (
                <FormItem>
                  <FormLabel>Instruments</FormLabel>
                  <div className="grid grid-cols-2 gap-2">
                    {instrumentOptions.map((instrument) => (
                      <FormField
                        key={instrument}
                        control={form.control}
                        name="instruments"
                        render={({ field }) => (
                          <FormItem className="flex items-center space-x-2">
                            <FormControl>
                              <Checkbox
                                checked={field.value?.includes(instrument)}
                                onCheckedChange={(checked) => {
                                  const value = field.value || [];
                                  if (instrument === "other") {
                                    setShowOtherInstrument(!!checked);
                                    if (!checked) {
                                      setOtherInstrumentValue("");
                                    }
                                  }
                                  if (checked) {
                                    field.onChange([...value, instrument]);
                                  } else {
                                    field.onChange(value.filter((item) => item !== instrument));
                                  }
                                }}
                              />
                            </FormControl>
                            <FormLabel className="text-sm capitalize font-normal">
                              {instrument === "other" ? "Other" : instrument}
                            </FormLabel>
                          </FormItem>
                        )}
                      />
                    ))}
                  </div>
                  {showOtherInstrument && (
                    <div className="mt-3">
                      <Input
                        placeholder="Enter custom instrument..."
                        value={otherInstrumentValue}
                        onChange={(e) => setOtherInstrumentValue(e.target.value)}
                        className="w-full"
                      />
                    </div>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <Collapsible className="space-y-4">
              <CollapsibleTrigger className="flex items-center space-x-2 text-sm font-medium">
                <ChevronDown className="h-4 w-4" />
                <span>Contact Details</span>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email Address</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="Enter email address" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Mobile Number</FormLabel>
                      <FormControl>
                        <Input type="tel" placeholder="Enter mobile number" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="instagram"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Instagram Handle</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter Instagram handle" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CollapsibleContent>
            </Collapsible>

            <FormField
              control={form.control}
              name="tier"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tier (Optional)</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a tier" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {tierOptions.map((tier) => (
                        <SelectItem key={tier} value={tier}>
                          {tier}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="priority"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Priority Participant</FormLabel>
                    <p className="text-sm text-muted-foreground">
                      Mark this participant as priority
                    </p>
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="lookingForBand"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Looking for a Band</FormLabel>
                    <p className="text-sm text-muted-foreground">
                      This participant is looking to join or form a band
                    </p>
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Add any notes about this participant for this event..."
                      className="min-h-[80px] resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

    </FormModal>
  );
}
